import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/store/auth.store";

/**
 * Log out and go to /login. Used by Settings and the sidebar user menu.
 *
 * The store clears the local session in a finally block and then rethrows a
 * failed server call, so this always ends on /login; a failure only means the
 * server did not confirm the logout, and the toast says that.
 */
export function useLogout(): {
	logout: () => Promise<void>;
	isLoggingOut: boolean;
} {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { logout: storeLogout } = useAuthStore();
	const [isLoggingOut, setIsLoggingOut] = useState(false);

	const logout = async (): Promise<void> => {
		setIsLoggingOut(true);
		try {
			await storeLogout();
		} catch {
			toast.error(t("settings.logoutServerFailed"));
		} finally {
			setIsLoggingOut(false);
			navigate("/login", { replace: true });
		}
	};

	return { logout, isLoggingOut };
}
